import { useEffect, useState } from 'react';
import Checkbox from '../../atoms/Checkbox';
import { Typography } from '../Typography';

export type RadioItem = {
  label: string;
  value: any;
  helperText?: string | string[];
};

interface MultipleRadioGroupProps {
  label?: string;
  items: RadioItem[];
  value?: RadioItem[] | null;
  onChange?: (value: RadioItem[]) => void;
  className?: string;
  disabled?: boolean;
  flex?: boolean;
  errorMessage?: string;
}

function MultipleRadioGroup({
  label,
  items,
  value,
  className,
  onChange,
  disabled,
  flex,
  errorMessage,
}: MultipleRadioGroupProps) {
  const [selectedItems, setSelectedItems] = useState<RadioItem[] | null>(
    value ? value : null
  );

  useEffect(() => {
    if (value && hasChanged()) {
      setSelectedItems(value);
    }
  }, [value]);

  useEffect(() => {
    if (selectedItems && onChange) {
      onChange(selectedItems);
    }
  }, [selectedItems]);

  function hasChanged() {
    return JSON.stringify(value) !== JSON.stringify(selectedItems);
  }

  function handleChange(item: RadioItem) {
    if (!selectedItems) {
      setSelectedItems([item]);
      return;
    }

    const index = selectedItems.findIndex(
      (selectedItem) => selectedItem.value === item.value
    );

    if (index === -1 && selectedItems) {
      setSelectedItems([...selectedItems, item]);
    } else {
      setSelectedItems(
        selectedItems.filter(
          (selectedItem) => selectedItem.value !== item.value
        )
      );
    }
  }

  return (
    <div className={`flex flex-col gap-4`}>
      {label && (
        <Typography className={`${disabled ? 'text-mono-color-dark' : ''}`}>
          {label}
        </Typography>
      )}
      <div
        className={`flex gap-4 ${
          disabled ? 'text-mono-color-medium' : ''
        } ${className} ${flex ? 'sm:flex-col md:flex-row' : 'flex-col'}`}
      >
        {items.map((item, index) => (
          <div className='' key={index}>
            <Checkbox
              key={`radio-${item.value}`}
              id={`radio-${item.value}`}
              label={item.label}
              value={item.value}
              variant='rounded'
              checked={
                !!selectedItems &&
                selectedItems.findIndex((i) => i.value === item.value) !== -1
              }
              disabled={disabled}
              onChange={() => {
                handleChange(item);
              }}
            />
            {item.helperText && (
              <div className='pl-14 text-mono-800'>
                {Array.isArray(item.helperText) ? (
                  <ul className='list-disc'>
                    {item.helperText.map((text, index) => (
                      <li key={index}>
                        <Typography size='small'>{text}</Typography>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Typography size='small'>{item.helperText}</Typography>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {errorMessage && (
        <Typography className='text-feedback-color-error-pure' size='small'>
          {errorMessage}
        </Typography>
      )}
    </div>
  );
}

export { MultipleRadioGroup };
